import React, { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Modal, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { HOME_PVP_ARENA_PATH } from '../navigationPaths';
import { sharePvpInviteLink } from '../utils/shareAchievementImage';
import type { QuizAttemptResponse } from '../services/api';
import SoloQuizResultsBody from './SoloQuizResultsBody';
import { playQuizSfxReward } from '../audio/quizSfx';

export interface AsyncHostIndividualResultsModalProps {
  show: boolean;
  onHide: () => void;
  roomName: string;
  quizTitle: string;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  /** XP credited for this round (performance + PvP rules); winner bonus comes later if applicable. */
  earnedXpPreview: number;
  /** Seconds taken for the quiz run (shown like solo results). */
  resultDurationSeconds?: number | null;
  /** Sala cancelada: não publicada no lobby por excesso de skips; sem XP. */
  publishBlockedDueToSkips?: boolean;
  /** Oponente: excesso de skips — partida não concluída; sala volta ao lobby; sem XP e sem reembolso. */
  opponentSkipsBlocked?: boolean;
  /** Full URL <c>/pvp-quiz/CODE</c> when the match is listed for opponents. */
  inviteUrl?: string;
  /** Breakdown igual ao solo (base, bônus, velocidade, poção, VIP, arena, app) — vindo do DTO do host. */
  quizResponse?: QuizAttemptResponse | null;
}

/**
 * Modal após o host concluir o quiz em Draft e publicar na Arena — mesmo corpo de resultados do solo + texto async + convite.
 */
const AsyncHostIndividualResultsModal: React.FC<AsyncHostIndividualResultsModalProps> = ({
  show,
  onHide,
  roomName,
  quizTitle,
  score,
  correctAnswers,
  totalQuestions,
  earnedXpPreview,
  resultDurationSeconds = null,
  publishBlockedDueToSkips = false,
  opponentSkipsBlocked = false,
  inviteUrl,
  quizResponse = null,
}) => {
  const { i18n, t } = useSafeTranslation();
  const navigate = useNavigate();
  const shareInviteAria = i18n.t('pvp.shareInviteLink', { defaultValue: 'Share invite link' });
  const rewardPlayedRef = useRef(false);

  const quizTitleDisplay = [roomName?.trim(), quizTitle?.trim()].filter(Boolean).join(' · ') || quizTitle;

  const successListed =
    show && !opponentSkipsBlocked && !publishBlockedDueToSkips && earnedXpPreview >= 0;

  useEffect(() => {
    if (!show) {
      rewardPlayedRef.current = false;
      return;
    }
    if (!successListed || earnedXpPreview <= 0) return;
    if (rewardPlayedRef.current) return;
    rewardPlayedRef.current = true;
    playQuizSfxReward();
  }, [show, successListed, earnedXpPreview]);

  const handleCopyInvite = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success(
        i18n.t('pvp.inviteLinkCopied', { defaultValue: 'Invite link copied.' })
      );
    } catch {
      toast.error(i18n.t('pvp.inviteLinkCopyFailed', { defaultValue: 'Could not copy link.' }));
    }
  };

  const handleShareInvite = async () => {
    if (!inviteUrl) return;
    try {
      const result = await sharePvpInviteLink({
        title: i18n.t('pvp.shareInviteTitle', { defaultValue: 'Quizter Club — PvP invite' }),
        text: i18n.t('pvp.shareInviteText', {
          quiz: quizTitle,
          defaultValue: 'Join my PvP match for {{quiz}}:',
        }),
        url: inviteUrl,
      });
      if (result === 'shared') {
        toast.success(
          i18n.t('pvp.shareInviteOpened', { defaultValue: 'Share sheet opened.' })
        );
      }
    } catch {
      try {
        await navigator.clipboard.writeText(inviteUrl);
        toast.success(
          i18n.t('pvp.inviteLinkCopied', { defaultValue: 'Invite link copied.' })
        );
      } catch {
        toast.error(
          i18n.t('pvp.shareInviteFailed', { defaultValue: 'Could not share or copy link.' })
        );
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        /* Intentionally no-op: dismiss only via footer actions */
      }}
      backdrop="static"
      keyboard={false}
      centered
      fullscreen="md-down"
      size="lg"
      className="solo-quiz-results-modal"
    >
      <Modal.Header closeButton={false}>
        <Modal.Title>
          {opponentSkipsBlocked
            ? i18n.t('pvp.asyncOpponentModalTitleSkipped', {
                defaultValue: 'Partida não concluída',
              })
            : publishBlockedDueToSkips
              ? i18n.t('pvp.asyncHostModalTitleSkipped', {
                  defaultValue: 'Match not published',
                })
              : t('quiz.quizResults', 'Quiz Results')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {opponentSkipsBlocked ? (
          <>
            <Alert variant="warning" className="mb-3">
              {i18n.t('pvp.asyncOpponentSkippedMatchBlocked', {
                defaultValue:
                  'Você pulou mais da metade das perguntas. Esta sala não foi concluída',
              })}
            </Alert>
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="fw-semibold mb-1">
                {i18n.t('pvp.asyncHostNoXpTitle', {
                  defaultValue: 'Experience (PvP)',
                })}
              </div>
              <div className="h4 mb-0 text-muted">
                {i18n.t('pvp.asyncHostXpZero', {
                  defaultValue: '0 XP',
                })}
              </div>
              <p className="small text-muted mt-2 mb-0">
                {i18n.t('pvp.asyncOpponentNoXpExplain', {
                  defaultValue:
                    'Nenhuma experiência é ganha quando a partida não é concluída. Entre em uma nova sala e tente de novo com menos pulos.',
                })}
              </p>
            </div>
          </>
        ) : publishBlockedDueToSkips ? (
          <>
            <Alert variant="warning" className="mb-3">
              {i18n.t('pvp.asyncHostSkippedPublishBlocked', {
                defaultValue:
                  'You skipped more than half of the questions. This room was not published to the Arena lobby and was cancelled.',
              })}
            </Alert>
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="fw-semibold mb-1">
                {i18n.t('pvp.asyncHostNoXpTitle', {
                  defaultValue: 'Experience (PvP)',
                })}
              </div>
              <div className="h4 mb-0 text-muted">
                {i18n.t('pvp.asyncHostXpZero', {
                  defaultValue: '0 XP',
                })}
              </div>
              <p className="small text-muted mt-2 mb-0">
                {i18n.t('pvp.asyncHostNoXpExplain', {
                  defaultValue:
                    'No experience is earned when the match is not published. Create a new room and try again with fewer skips.',
                })}
              </p>
            </div>
          </>
        ) : (
          <>
            <SoloQuizResultsBody
              hasTooManySkips={false}
              isQuizCompleted
              quizTitleDisplay={quizTitleDisplay}
              correctAnswers={correctAnswers}
              totalQuestions={totalQuestions}
              score={score}
              earnedXP={earnedXpPreview}
              quizResponse={quizResponse}
              resultDurationSeconds={resultDurationSeconds}
              hideInstallAppTeaser
            />
            <p className="small text-start text-muted mt-3 mb-2">
              {i18n.t('pvp.asyncHostArenaListedLine', {
                defaultValue: 'Your match is now listed in the Arena for opponents.',
              })}
            </p>
            <p className="small text-start text-muted mb-0">
              {i18n.t('pvp.asyncHostNoWinnerNotifyLine', {
                defaultValue: 'There is no winner yet. When the match ends, you will be notified of the result.',
              })}
            </p>
            {inviteUrl ? (
              <div className="border rounded p-3 mt-3 bg-white text-center">
                <div className="fw-semibold mb-2">
                  {i18n.t('pvp.inviteOpponentHeading', {
                    defaultValue: 'Invite an opponent',
                  })}
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-center">
                  <Button variant="outline-primary" size="sm" type="button" onClick={() => void handleCopyInvite()}>
                    <i className="bi bi-link-45deg me-1" aria-hidden />
                    {i18n.t('pvp.copyInviteLink', { defaultValue: 'Copy link' })}
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    type="button"
                    title={shareInviteAria}
                    aria-label={shareInviteAria}
                    onClick={() => void handleShareInvite()}
                  >
                    <i className="bi bi-share" aria-hidden />
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between flex-wrap gap-2">
        <Button
          variant="outline-secondary"
          onClick={() => {
            onHide();
            navigate('/');
          }}
        >
          {i18n.t('quiz.backToHome', { defaultValue: 'Back to Home' })}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onHide();
            navigate(HOME_PVP_ARENA_PATH);
          }}
        >
          {i18n.t('pvp.asyncHostGoArena', { defaultValue: 'Back to Arena' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AsyncHostIndividualResultsModal;
